import { Chart as ChartJS, ChartType, Plugin } from 'chart.js'

const LABEL_OFFSET = 6

export type ValueFormatter = (value: number) => string

export const defaultValueFormatter: ValueFormatter = (value) => value.toLocaleString()

export type BucketLabel = string | string[]

export const toLines = (label: string): BucketLabel => {
  const [from, to] = label.split(' to ')
  return to === undefined ? label : [`${from} to`, to]
}

export interface BucketSeries {
  label: string
  color: string | string[]
  values: number[]
}

interface BarValueLabelsOptions {
  formatValue?: ValueFormatter
  formatLines?: (value: number, index: number) => string[]
  color?: string
}

declare module 'chart.js' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PluginOptionsByType<TType extends ChartType> {
    barValueLabels?: BarValueLabelsOptions
  }
}

const LABEL_LINE_HEIGHT = 13

const barValueLabels: Plugin<'bar', BarValueLabelsOptions> = {
  id: 'barValueLabels',
  afterDatasetsDraw(chart, _args, options) {
    const { ctx } = chart
    const formatValue = options.formatValue ?? defaultValueFormatter

    ctx.save()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    ctx.font = `600 11px ${ChartJS.defaults.font.family}`

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (!chart.isDatasetVisible(datasetIndex)) {
        return
      }

      const { backgroundColor } = dataset

      chart.getDatasetMeta(datasetIndex).data.forEach((bar, index) => {
        const value = dataset.data[index]

        if (typeof value !== 'number' || value === 0) {
          return
        }

        const barColor = Array.isArray(backgroundColor) ? backgroundColor[index] : backgroundColor
        ctx.fillStyle = options.color ?? (typeof barColor === 'string' ? barColor : '#000')

        const lines = options.formatLines?.(value, index) ?? [formatValue(value)]

        lines.forEach((line, lineIndex) => {
          const linesBelow = lines.length - 1 - lineIndex
          ctx.fillText(line, bar.x, bar.y - LABEL_OFFSET - linesBelow * LABEL_LINE_HEIGHT)
        })
      })
    })

    ctx.restore()
  },
}

export const bucketBarChartPlugins = [barValueLabels]
